<script lang="ts">
    import { formatDateOnly, formatMoney, formatTimeOnly } from "@frontend/Pages/Formats";
    import { 
        ApiResult, 
        type ApiResultOfWalletTransaction, 
        type User, 
        type WithdrawPinRequest 
    } from "@frontend/walletapi";
    import { service } from "@frontend/services";
    import { onDestroy, onMount } from "svelte";
    import { Timeout } from "@frontend/timeout";
    import { CashMode, settings } from "@frontend/settings";
    import { GetCachedToken } from "@frontend/user";
    import { translate, currentLanguage } from "../Translator";
    import { me } from "@frontend/user";
    
    export let player: User;
    export let dismiss: () => void;

    let result: ApiResultOfWalletTransaction | undefined;
    let pinWithdraw: string = '';

    let printing: boolean = false;
    let printingTimeout: Timeout = new Timeout(2000, () => printing = false);
    
    onDestroy(() => {
        printingTimeout.Dispose();
    });

    function handlePinInput(event: Event) {
        const input = event.target as HTMLInputElement;
        // Solo permitir números
        const numbersOnly = input.value.replace(/[^0-9]/g, '');
        pinWithdraw = numbersOnly;
        input.value = numbersOnly;
    }
    
    async function Withdraw() {
        // Usar MoneyTransaction Balance en lugar de CashableBalance
        const amount = player?.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance?.InCents ?? 0;
        const withdrawData: WithdrawPinRequest = {
            UserId: player.Id,
            OperatorId : $me?.Id,
            Amount: {
                InCents: amount,
            },
            Pin: pinWithdraw
        };

        result = await service.walletapi.withdrawalPin({
            Token: sessionStorage.token,
            Data: withdrawData
        });

        if (result?.Success) {
            printing = true;
            printingTimeout.Start();
        }
    }

    async function PrintTransaction() {
        if (!result?.Success || !result.Data) {
            return;
        }

        let printResult: ApiResult = await service.walletapi.printTransaction({
            Token: GetCachedToken(),
            Data: {
                Id: result.Data.Id
            }
        });

        if (printResult.Success) {
            printing = true;
            printingTimeout.Start();
        }
    }
</script>

<h2 style="text-align: center;"> {$translate('Withdraw Cashier')}</h2>
   
{#if result?.Success && result.Data}
    <div class="ui positive message">
        <div class="header">
        {$translate('Withdraw')} {formatMoney(result.Data.CashableAmount)} {$translate('Successful')}
        </div>
    </div>

    <div class="ui segment" style="display: flex;">
        <table class="receit-table" style="flex-grow: 1;">
            <tr>
                <td class="receit-title">Trans. ID</td>
                <td class="receit-value" style="text-align: right">#{result.Data.Id}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Player Id')}</td>
                <td class="receit-value" style="text-align: right">#{result.Data.UserId}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Transaction date')}</td>
                <td class="receit-value" style="text-align: right">{formatDateOnly(result.Data.TransactionTime)}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Transaction date')}</td>
                <td class="receit-value" style="text-align: right">{formatTimeOnly(result.Data.TransactionTime)}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Type of transaction')}</td>
                <td class="receit-value" style="text-align: right">{$translate('Withdraw')}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Amount')}</td>
                <td class="receit-value" style="text-align: right">{formatMoney(player?.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance)}</td>
            </tr>
            <tr>
                <td class="receit-title">{$translate('Money Balance')}</td>
                <td class="receit-value" style="text-align: right">{formatMoney(result.Data.CashableBalance)}</td>
            </tr>
        </table>
    </div>

    <div style="display: flex; justify-content: center; gap: 10px;">
        <button class="ui primary fluid button" on:click={dismiss}>{$translate('Dismiss')}</button>
        {#if printing}
            <button class="ui fluid disabled button"><i class="loading spinner icon"></i>Printing</button>
        {:else}
            <button class="ui fluid button" on:click={PrintTransaction}>{$translate('Print')}</button>
        {/if}
    </div>

{:else}
    <table class="ui very basic very compact unstackable table player-info-table">
        <tr>
            <td>{$translate('Player Id')}</td>
            <td style="text-align: right">#{player.Id}</td>
        </tr>
        <tr>
            <td>{$translate('First Name')}</td>
            <td style="text-align: right">{player.FirstName ?? ""} {player.LastName ?? ""}</td>
        </tr>
        <tr>
            <td>{$translate('E-mail')}</td>
            <td style="text-align: right">{player.Email ?? ""}</td>
        </tr>
                    <tr>
                <td>{$translate('Withdrawal Amount')}</td>
                <td style="text-align: right; font-weight: bold; font-size: 1.2em;">
                    {formatMoney(player?.LastWalletTransaction?.MoneyTransaction?.[0]?.Balance)}
                </td>
            </tr>
    </table>

    <div class="ui form">
        {#if result && !result.Success}
            <div class="ui negative message">
                {result.ErrorMessage ?? "Unknown error during transaction"}
            </div>
        {/if}

        <div class="field">
            <label>{$translate('Pin')}</label>
            <input 
                type="text" 
                class="pin-input"
                bind:value={pinWithdraw} 
                on:input={handlePinInput}
                placeholder="Enter PIN"
            >
        </div>

        <div style="display: flex; gap: 10px; margin-top: 20px;">
            <div class="field" style="flex: 1;">
                <button class="ui negative fluid button" on:click={dismiss}>Cancel</button>
            </div>
            <div class="field" style="flex: 1;">
                <button 
                    class="ui positive fluid button" 
                    on:click={Withdraw}
                    disabled={!pinWithdraw}
                >
                    Withdraw
                </button>
            </div>
        </div>
    </div>
{/if}

<style>
    .receit-table {
        width: 100%;
        border-collapse: collapse;
    }
    .receit-title {
        font-weight: normal;
        text-align: left;
        padding: 4px 8px;
        border-bottom: 1px solid #eee;
    }
    .receit-value {
        font-weight: normal;
        padding: 4px 8px;
        border-bottom: 1px solid #eee;
    }
    .ui.segment {
        padding: 1em;
        box-shadow: none;
        border: 1px solid #eee;
    }

    @media (max-width: 767px) {
        .ui.table.player-info-table {
            font-size: 1.5em;
        }

        .pin-input {
            font-size: 16px !important;
        }

        .ui.button {
            min-height: 44px;
        }
    }
</style>
